<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorCode, apiErrorMessage } from '../../../../api/coLiving/errors';
import {
  getCondoKnowledge,
  putCondoKnowledge,
} from '../../../../api/coLiving/documents';
import { FIELD_CLASS } from '../constants';
import {
  emptyCondo,
  condoToForm,
  validateCondo,
  condoPayload,
} from '../condoForm';
import FormField from './FormField.vue';

export default {
  name: 'CondoModal',
  components: { BaseModal, FormField },
  props: {
    condo: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      form: this.condo ? condoToForm(this.condo) : emptyCondo(),
      errors: {},
      isSubmitting: false,
      // The condo's knowledge graph setting (edit only). `knowledge` is what
      // the server said when the dialog opened; null until it answers.
      knowledge: null,
      graphEnabled: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.condo;
    },
    // The knowledge service refuses to change the setting while any document
    // of this condo is in the bot.
    isGraphLocked() {
      return !!this.knowledge && this.knowledge.graph_locked;
    },
  },
  mounted() {
    if (this.isEdit) this.loadKnowledge();
  },
  methods: {
    ...mapActions('coLivingCondos', ['addCondo', 'editCondo', 'archiveCondo']),
    async loadKnowledge() {
      try {
        const { data } = await getCondoKnowledge(this.condo.id);
        this.knowledge = data;
        this.graphEnabled = !!data.graph_enabled;
      } catch (err) {
        // No switch rather than a wrong one; the rest of the form still works.
        this.knowledge = null;
      }
    },
    // A second call, made only when the switch changed (like Active).
    async saveGraph() {
      if (
        !this.knowledge ||
        this.graphEnabled === this.knowledge.graph_enabled
      ) {
        return;
      }
      await putCondoKnowledge(this.condo.id, this.graphEnabled);
    },
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.CONDOS.ERRORS.${this.errors[field]}`)
        : '';
    },
    // Archive/restore is its own call, made only when the switch changed.
    async saveActive() {
      if (this.isEdit && this.form.active !== this.condo.active) {
        await this.archiveCondo({
          id: this.condo.id,
          active: this.form.active,
        });
      }
    },
    showServerError(err) {
      const code = apiErrorCode(err);
      if (code === 'address_not_found') {
        this.errors = { address: 'ADDRESS_NOT_FOUND' };
      } else if (code === 'name_taken') {
        this.errors = { name: 'NAME_TAKEN' };
      } else {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.CONDOS.SAVE_FAILED'));
      }
    },
    async onSubmit() {
      this.errors = validateCondo(this.form);
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      const body = condoPayload(this.form, { isEdit: this.isEdit });
      try {
        let saved;
        if (this.isEdit) {
          saved = await this.editCondo({ id: this.condo.id, fields: body });
          await this.saveActive();
          try {
            await this.saveGraph();
          } catch (err) {
            // The condo itself is saved: tell the list, keep the dialog open
            // on the refusal (usually graph_locked).
            this.$emit('saved', saved);
            useAlert(
              apiErrorMessage(
                this,
                err,
                'CO_LIVING.CONDOS.KNOWLEDGE.SAVE_FAILED'
              )
            );
            this.loadKnowledge();
            return;
          }
        } else {
          saved = await this.addCondo(body);
        }
        useAlert(this.$t('CO_LIVING.CONDOS.SAVED'));
        this.$emit('saved', saved);
        this.$emit('close');
      } catch (err) {
        this.showServerError(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{
        isEdit
          ? $t('CO_LIVING.CONDOS.EDIT_CONDO')
          : $t('CO_LIVING.CONDOS.ADD_CONDO')
      }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-condo-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        :label="$t('CO_LIVING.CONDOS.FIELDS.NAME')"
        for-id="co-living-condo-name"
        :error="errorText('name')"
      >
        <input
          id="co-living-condo-name"
          v-model="form.name"
          type="text"
          maxlength="120"
          :class="[fieldClass, errors.name ? 'border-red-500' : '']"
          data-testid="co-living-condo-name"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.CONDOS.FIELDS.ADDRESS')"
        for-id="co-living-condo-address"
        :error="errorText('address')"
        :hint="$t('CO_LIVING.CONDOS.FIELDS.ADDRESS_HINT')"
      >
        <input
          id="co-living-condo-address"
          v-model="form.address"
          type="text"
          :class="[fieldClass, errors.address ? 'border-red-500' : '']"
          data-testid="co-living-condo-address"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.CONDOS.FIELDS.DESCRIPTION')"
        for-id="co-living-condo-description"
      >
        <textarea
          id="co-living-condo-description"
          v-model="form.description"
          rows="3"
          :class="fieldClass"
          data-testid="co-living-condo-description"
        />
      </FormField>

      <label
        class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
      >
        <input
          v-model="form.active"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-condo-active"
        />
        {{ $t('CO_LIVING.COMMON.ACTIVE') }}
      </label>

      <div v-if="isEdit && knowledge" data-testid="co-living-condo-knowledge">
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <input
            v-model="graphEnabled"
            type="checkbox"
            class="mb-0"
            :disabled="isGraphLocked"
            data-testid="co-living-condo-graph"
          />
          {{ $t('CO_LIVING.CONDOS.KNOWLEDGE.GRAPH') }}
        </label>
        <p
          class="mt-1 text-xs text-slate-500 dark:text-slate-400"
          data-testid="co-living-condo-graph-hint"
        >
          {{
            isGraphLocked
              ? $t('CO_LIVING.CONDOS.KNOWLEDGE.LOCKED')
              : $t('CO_LIVING.CONDOS.KNOWLEDGE.HINT')
          }}
        </p>
      </div>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-condo-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
